// src/components/document-sets/DocumentSetSidebarPill.js
import React, { useState } from 'react';
import { FolderLock, LockKeyhole, LockKeyholeOpen, Trash2, SquarePen } from 'lucide-react';
import DocumentSetInlineEditor from './DocumentSetInlineEditor';

/**
 * Custom Icon Button for Pill - matches IconButton behavior with custom hover color
 */
const PillIconButton = ({ children, onClick, title, hoverColor, defaultColor, hoverTextColor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="p-1 rounded-app-2xs flex items-center justify-center flex-shrink-0"
      style={{
        backgroundColor: isHovered ? hoverColor : 'transparent',
        color: isHovered ? hoverTextColor : defaultColor,
        opacity: isHovered ? 1 : 0.7
      }}
      title={title}
    >
      {React.cloneElement(children, {
        className: 'w-3 h-3'
      })}
    </button>
  );
};

/**
 * Document Set Pill for Sidebar
 *
 * FIXED: Uses CSS class (.pill-selected) instead of inline styles for transform
 * This ensures the same rendering context as btn-primary hover states
 */
const DocumentSetSidebarPill = ({
  documentSet,
  isSelected = false,
  isArchived = false,
  onSelect,
  onToggleArchive,
  onDelete,
  onUpdateName,
  documentSets = [],
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLockHovered, setIsLockHovered] = useState(false);

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(documentSet.id);
  };

  const handleToggleArchive = (e) => {
    e.stopPropagation();
    onToggleArchive?.(documentSet.id);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(documentSet.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleUpdateName = async (setId, newName) => {
    await onUpdateName?.(setId, newName);
    setIsEditing(false);
    setIsHovered(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsHovered(false);
  };

  if (isEditing) {
    return (
      <DocumentSetInlineEditor
        existingSet={documentSet}
        onUpdateSet={handleUpdateName}
        onCancel={handleCancelEdit}
        documentSets={documentSets}
      />
    );
  }

  const fontWeight = 'font-medium';

  const getColors = () => {
    return {
      bg: documentSet.color.bg,
      text: documentSet.color.text,
      border: documentSet.color.border,
    };
  };

  const colors = getColors();

  const getBorderColor = () => {
    if (isSelected || isHovered) {
      return colors.text;
    }
    return colors.border;
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        pill-base
        ${isSelected ? 'pill-raised' : ''}
        group/pill inline-flex items-center justify-between max-w-full
        px-3 rounded-full text-app-xs border h-[24px]
        ${fontWeight}
      `}
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
        borderColor: isSelected ? colors.text : getBorderColor(),
        // Note: boxShadow moved to CSS class to work with transform
        '--pill-shadow-color': colors.text,
      }}
    >
      {/* Left side: Archive icon (if archived) + Set name */}
      <div className="flex items-center gap-app-xs min-w-0">
        {isArchived && (
          <button
            onClick={handleToggleArchive}
            onMouseEnter={() => setIsLockHovered(true)}
            onMouseLeave={() => setIsLockHovered(false)}
            className="p-1 transition-opacity flex-shrink-0"
            style={{ opacity: isLockHovered ? 1 : 0.7 }}
            title="Reactivate tag"
          >
            {isLockHovered ? (
              <LockKeyholeOpen className="ml-[-6px] w-3 h-3" />
            ) : (
              <LockKeyhole className="ml-[-6px] w-3 h-3" />
            )}
          </button>
        )}
        <span className="select-none truncate">
          {documentSet.name}
        </span>
      </div>

      {/* Right side: Action buttons (visible on hover, only if NOT archived) */}
      {!isArchived && isHovered && (
        <div className="pl-app-md mr-[-6px] flex items-center gap-0 flex-shrink-0">
          <PillIconButton
            onClick={handleEdit}
            title="Edit tag name"
            hoverColor={documentSet.color.hover}
            defaultColor={documentSet.color.text}
            hoverTextColor={documentSet.color.text}
          >
            <SquarePen />
          </PillIconButton>

          <PillIconButton
            onClick={handleToggleArchive}
            title="Archive tag"
            hoverColor={documentSet.color.hover}
            defaultColor={documentSet.color.text}
            hoverTextColor={documentSet.color.text}
          >
            <FolderLock style={{ fill: 'none' }} />
          </PillIconButton>

          <PillIconButton
            onClick={handleDelete}
            title="Delete tag"
            hoverColor={documentSet.color.hover}
            defaultColor={documentSet.color.text}
            hoverTextColor={documentSet.color.text}
          >
            <Trash2/>
          </PillIconButton>
        </div>
      )}
    </button>
  );
};

export default DocumentSetSidebarPill;